/**
 * The batch's progress bar and legend, drawn from `statusCounts`.
 *
 * Those counts are derived per request server-side, so they are exact — which
 * is why this shows "310 of 494 sent · 6 failed" rather than an indeterminate
 * spinner. The bar only ever renders the statuses the batch actually has.
 */

import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { batchStyles } from "../BulkSend.styles";
import tokens from "../BulkSend.tokens";
import { BulkRowStatus } from "../BulkSendTypesAndValidation";

const p = tokens.palette;
const s = batchStyles;

/** Ordered so the bar reads left-to-right: done, in flight, waiting, wrong. */
const SEGMENTS: {
  status: BulkRowStatus;
  label: string;
  color: string;
}[] = [
  { status: "Sent", label: "sent", color: p.success },
  { status: "Sending", label: "sending", color: p.segmentDelivered },
  { status: "Pending", label: "queued", color: p.segmentQueued },
  { status: "Failed", label: "failed", color: p.danger },
  { status: "Cancelled", label: "cancelled", color: p.muted },
];

interface Props {
  statusCounts?: Partial<Record<BulkRowStatus, number>>;
  totalRows: number;
}

const BatchProgress: React.FC<Props> = ({ statusCounts, totalRows }) => {
  const rows = useMemo(
    () =>
      SEGMENTS.map((segment) => ({
        ...segment,
        count: statusCounts?.[segment.status] ?? 0,
      })).filter((segment) => segment.count > 0),
    [statusCounts],
  );

  const counted = rows.reduce((sum, row) => sum + row.count, 0);
  const denominator = Math.max(totalRows, counted, 1);

  return (
    <Box sx={s.body}>
      <Box
        sx={s.bar}
        role="img"
        aria-label={
          rows.length
            ? rows.map((row) => `${row.count} ${row.label}`).join(", ")
            : "Nothing sent yet"
        }
      >
        {rows.map((row) => (
          <Box
            key={row.status}
            sx={s.barSegment(`${(row.count / denominator) * 100}%`, row.color)}
          />
        ))}
      </Box>

      <Box sx={s.legend}>
        {rows.length === 0 ? (
          <Box component="span" sx={s.legendLabel}>
            Nothing has gone out yet.
          </Box>
        ) : (
          rows.map((row) => (
            <Box key={row.status} sx={s.legendItem}>
              <Box sx={s.legendDot(row.color)} />
              <Box component="span" sx={s.legendNumber}>
                {row.count}
              </Box>
              <Box component="span" sx={s.legendLabel}>
                {row.label}
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default BatchProgress;
